/**
 * IMpb — USPS Intelligent Mail package barcode construction, validation, and parsing.
 *
 * SOURCE OF TRUTH: docs/usps-spec/BarcodePackageIMSpec.pdf
 *   "Barcode, Package, Intelligent Mail(R) Specification Rev E"
 *   USPS2000508, CAGE CODE 27085, dated 12/29/2011, section 8.5 (Tables 6-12).
 *   Extracted text: docs/usps-spec/impb-spec.txt
 *
 * The FIELD WIDTHS below are transcribed verbatim from that specification. They are
 * NOT assumptions. Do not "simplify" them.
 *
 * SIMULATION BOUNDARY (important, keep this honest):
 *   The structure implemented here is spec-accurate, but this project is a DEMO. The
 *   Mailer IDs are fabricated, we are not a licensed USPS mailer, and output from this
 *   module MUST NOT be used to produce real mailing labels or tendered mail.
 *
 * Key spec facts encoded here (each cost a mistake if guessed):
 *   - The Channel Application Identifier SELECTS the MID width: "92" => 9-digit MID,
 *     "93" => 6-digit MID. MID width is not an independent choice.
 *   - The "420" + ZIP postal routing AI is ENCODED in the barcode but SUPPRESSED from the
 *     human-readable representation. A 30-digit C03 therefore displays as 22 digits.
 *   - MOD 10 is the *data* check digit (computed over the PIC digits).
 *     MOD 103 is the separate Code128 *symbology* check digit, computed by the encoder,
 *     and is NOT part of the data string. Never conflate the two.
 *   - Service Type Code is always 3 digits.
 *   - The tracking number is officially the PIC (Package Identification Code) =
 *     Application Identifier + Service Type Code + construct data + MOD 10 check digit.
 */

'use strict';

/**
 * Commercial mail data constructs, verbatim from Spec Rev E section 8.5.
 *
 * `fields` is ordered and its lengths sum to `totalDigits`.
 * `routingPrefix` fields (ai420 + zip) are encoded but suppressed from human-readable text.
 */
const CONSTRUCTS = Object.freeze({
  // Table 6 - C01 (9-digit Mailer ID, 9-digit ZIP)
  C01: {
    id: 'C01',
    description: 'Commercial Mail, 9-digit Mailer ID, 9-digit ZIP (ZIP+4)',
    channelAI: '92',
    midLength: 9,
    zipLength: 9,
    serialLength: 7,
    totalDigits: 34,
    hasRouting: true,
  },
  // Table 7 - C02 (9-digit Mailer ID, 5-digit ZIP) - note the LONGER 11-digit serial
  C02: {
    id: 'C02',
    description: 'Commercial Mail, 9-digit Mailer ID, 5-digit ZIP, extended serial',
    channelAI: '92',
    midLength: 9,
    zipLength: 5,
    serialLength: 11,
    totalDigits: 34,
    hasRouting: true,
  },
  // Table 8 - C03 (9-digit Mailer ID, 5-digit ZIP)
  C03: {
    id: 'C03',
    description: 'Commercial Mail, 9-digit Mailer ID, 5-digit ZIP',
    channelAI: '92',
    midLength: 9,
    zipLength: 5,
    serialLength: 7,
    totalDigits: 30,
    hasRouting: true,
  },
  // Table 9 - C04 (9-digit Mailer ID, no ZIP) - the compact 22-digit tracking-only form
  C04: {
    id: 'C04',
    description: 'Commercial Mail, 9-digit Mailer ID, no ZIP (tracking only)',
    channelAI: '92',
    midLength: 9,
    zipLength: 0,
    serialLength: 7,
    totalDigits: 22,
    hasRouting: false,
  },
  // Table 10 - C05 (6-digit Mailer ID, 9-digit ZIP) - channel AI 93, 10-digit serial
  C05: {
    id: 'C05',
    description: 'Commercial Mail, 6-digit Mailer ID, 9-digit ZIP (ZIP+4)',
    channelAI: '93',
    midLength: 6,
    zipLength: 9,
    serialLength: 10,
    totalDigits: 34,
    hasRouting: true,
  },
  // Table 11 - C06 (6-digit Mailer ID, 5-digit ZIP) - the WIDEST serial in the spec at 14 digits.
  // Note the spec's worked example renders it as `1234567890####`, i.e. the printed sample pads to
  // 14 rather than showing 14 significant digits; the FIELD WIDTH is 14 and that is what matters
  // here. This is the largest single-combination serial space of any commercial construct.
  C06: {
    id: 'C06',
    description: 'Commercial Mail, 6-digit Mailer ID, 5-digit ZIP, extended serial',
    channelAI: '93',
    midLength: 6,
    zipLength: 5,
    serialLength: 14,
    totalDigits: 34,
    hasRouting: true,
  },
  // Table 12 - C07 (6-digit Mailer ID, 5-digit ZIP) - channel AI 93 selects the 6-digit MID
  C07: {
    id: 'C07',
    description: 'Commercial Mail, 6-digit Mailer ID, 5-digit ZIP',
    channelAI: '93',
    midLength: 6,
    zipLength: 5,
    serialLength: 10,
    totalDigits: 30,
    hasRouting: true,
  },
});

const ROUTING_AI = '420';
const STC_LENGTH = 3;
const CHECK_DIGIT_LENGTH = 1;

class ImpbError extends Error {
  constructor(message) {
    super(message);
    this.name = 'ImpbError';
  }
}

/** Assert a value is a digit string of an exact length. */
function assertDigits(value, length, label) {
  const s = String(value);
  if (!/^[0-9]+$/.test(s)) {
    throw new ImpbError(`${label} must contain only digits 0-9, got ${JSON.stringify(value)}`);
  }
  if (s.length !== length) {
    throw new ImpbError(`${label} must be exactly ${length} digits, got ${s.length} (${s})`);
  }
  return s;
}

/** Left-pad a numeric serial into its fixed field width. Throws if it does not fit. */
function padSerial(serial, length) {
  const s = String(serial);
  if (!/^[0-9]+$/.test(s)) {
    throw new ImpbError(`serial must be numeric, got ${JSON.stringify(serial)}`);
  }
  if (s.length > length) {
    throw new ImpbError(
      `serial ${s} overflows the ${length}-digit serial field for this construct ` +
        `(max ${'9'.repeat(length)})`
    );
  }
  return s.padStart(length, '0');
}

/**
 * MOD 10 check digit over the PIC digits (USPS / GS1 standard weighting).
 *
 * Per spec: sum digits with alternating weights of 3 and 1, applying weight 3 to the
 * RIGHTMOST digit of the payload and alternating leftward; the check digit is the value
 * that brings the total to the next multiple of 10.
 *
 * NOTE: this is the DATA check digit. The Code128 MOD 103 symbology check digit is
 * computed by the barcode encoder and is not part of this string.
 *
 * @param {string} digits payload digits WITHOUT the check digit
 * @returns {number} single check digit 0-9
 */
function mod10CheckDigit(digits) {
  const s = String(digits);
  if (!/^[0-9]+$/.test(s)) {
    throw new ImpbError('mod10CheckDigit requires a digit-only string');
  }
  let sum = 0;
  // Weight 3 applies to the rightmost payload digit, alternating 3,1,3,1... leftward.
  for (let i = 0; i < s.length; i += 1) {
    const fromRight = s.length - 1 - i;
    const weight = fromRight % 2 === 0 ? 3 : 1;
    sum += Number(s[i]) * weight;
  }
  return (10 - (sum % 10)) % 10;
}

/**
 * Build a complete IMpb barcode for a given construct.
 *
 * @param {object} opts
 * @param {string} opts.construct one of C01|C02|C03|C04|C07
 * @param {string} opts.serviceTypeCode 3 digits
 * @param {string} opts.mailerId 6 or 9 digits (must match the construct)
 * @param {string|number} opts.serial the vended serial number
 * @param {string} [opts.zip] 5 or 9 digits, required when the construct has routing
 * @returns {object} full barcode representation
 */
function buildBarcode(opts) {
  const { construct, serviceTypeCode, mailerId, serial, zip } = opts || {};
  const spec = CONSTRUCTS[construct];
  if (!spec) {
    throw new ImpbError(
      `unknown construct ${JSON.stringify(construct)}; valid: ${Object.keys(CONSTRUCTS).join(', ')}`
    );
  }

  const stc = assertDigits(serviceTypeCode, STC_LENGTH, 'serviceTypeCode');
  const mid = assertDigits(mailerId, spec.midLength, `mailerId (construct ${spec.id})`);
  const ser = padSerial(serial, spec.serialLength);

  let zipDigits = '';
  if (spec.hasRouting) {
    if (zip === undefined || zip === null || zip === '') {
      throw new ImpbError(`construct ${spec.id} requires a ${spec.zipLength}-digit zip`);
    }
    zipDigits = assertDigits(zip, spec.zipLength, `zip (construct ${spec.id})`);
  } else if (zip) {
    throw new ImpbError(`construct ${spec.id} does not carry a ZIP, but zip was supplied`);
  }

  // The PIC payload: channel AI + STC + MID + serial. The check digit is computed over
  // this payload. The routing AI/ZIP is a separate encoded element and is NOT part of the
  // PIC used for tracking.
  const picPayload = `${spec.channelAI}${stc}${mid}${ser}`;
  const checkDigit = String(mod10CheckDigit(picPayload));
  const pic = `${picPayload}${checkDigit}`;

  // Full encoded data string, including the suppressed routing element when present.
  const routing = spec.hasRouting ? `${ROUTING_AI}${zipDigits}` : '';
  const full = `${routing}${pic}`;

  if (full.length !== spec.totalDigits) {
    // Guards against a future edit silently breaking a construct.
    throw new ImpbError(
      `internal: construct ${spec.id} produced ${full.length} digits, spec requires ${spec.totalDigits}`
    );
  }

  return {
    construct: spec.id,
    /** Full digit string as ENCODED in the barcode (routing AI + ZIP included). */
    encoded: full,
    /** The Package Identification Code — the tracking number. */
    pic,
    /**
     * Human-readable representation. Per spec the 420+ZIP routing element is SUPPRESSED,
     * so this is the PIC only, grouped in 4s.
     */
    humanReadable: groupDigits(pic),
    fields: {
      routingAI: spec.hasRouting ? ROUTING_AI : null,
      zip: spec.hasRouting ? zipDigits : null,
      channelAI: spec.channelAI,
      serviceTypeCode: stc,
      mailerId: mid,
      serial: ser,
      checkDigit,
    },
    totalDigits: spec.totalDigits,
    humanReadableDigits: pic.length,
    /** GS1-128 element string, FNC1 shown as {FNC1}. */
    gs1ElementString: buildGs1ElementString(spec, zipDigits, pic),
  };
}

/** Group a digit string into space-separated 4-digit groups, per spec display format. */
function groupDigits(digits) {
  return String(digits).replace(/(.{4})/g, '$1 ').trim();
}

/**
 * GS1-128 element string. FNC1 separates variable-length AI elements.
 * The encoder adds Start/Stop codes and the MOD 103 symbology check digit.
 */
function buildGs1ElementString(spec, zipDigits, pic) {
  const parts = [];
  if (spec.hasRouting) parts.push(`(${ROUTING_AI})${zipDigits}`);
  parts.push(`(${spec.channelAI})${pic.slice(spec.channelAI.length)}`);
  return parts.join('{FNC1}');
}

/**
 * Validate and parse an IMpb digit string (with or without the routing element).
 * @returns {{valid: boolean, reason?: string, parsed?: object}}
 */
function parseBarcode(input) {
  const digits = String(input).replace(/[\s-]/g, '');
  if (!/^[0-9]+$/.test(digits)) {
    return { valid: false, reason: 'contains non-digit characters' };
  }

  // Identify candidate constructs by total length, then by channel AI position.
  const candidates = Object.values(CONSTRUCTS).filter(
    (c) => c.totalDigits === digits.length || c.totalDigits - (c.hasRouting ? 3 + c.zipLength : 0) === digits.length
  );
  if (candidates.length === 0) {
    return {
      valid: false,
      reason:
        `length ${digits.length} matches no known construct ` +
        `(valid totals: ${[...new Set(Object.values(CONSTRUCTS).map((c) => c.totalDigits))].sort().join(', ')})`,
    };
  }

  for (const spec of candidates) {
    const hasRouting = digits.length === spec.totalDigits && spec.hasRouting;
    let rest = digits;
    let zipDigits = null;

    if (hasRouting) {
      if (!digits.startsWith(ROUTING_AI)) continue;
      zipDigits = digits.slice(3, 3 + spec.zipLength);
      rest = digits.slice(3 + spec.zipLength);
    }

    if (!rest.startsWith(spec.channelAI)) continue;

    const expectedPicLen = spec.channelAI.length + STC_LENGTH + spec.midLength + spec.serialLength + CHECK_DIGIT_LENGTH;
    if (rest.length !== expectedPicLen) continue;

    let o = spec.channelAI.length;
    const stc = rest.slice(o, (o += STC_LENGTH));
    const mid = rest.slice(o, (o += spec.midLength));
    const ser = rest.slice(o, (o += spec.serialLength));
    const check = rest.slice(o, o + CHECK_DIGIT_LENGTH);

    const expectedCheck = String(mod10CheckDigit(rest.slice(0, rest.length - 1)));
    if (check !== expectedCheck) {
      return {
        valid: false,
        reason: `MOD 10 check digit mismatch: found ${check}, expected ${expectedCheck}`,
        construct: spec.id,
      };
    }

    return {
      valid: true,
      parsed: {
        construct: spec.id,
        encoded: digits,
        pic: rest,
        humanReadable: groupDigits(rest),
        fields: {
          routingAI: hasRouting ? ROUTING_AI : null,
          zip: zipDigits,
          channelAI: spec.channelAI,
          serviceTypeCode: stc,
          mailerId: mid,
          serial: ser,
          checkDigit: check,
        },
      },
    };
  }

  return { valid: false, reason: 'no construct matched the AI / field layout' };
}

/** Max serial value a construct can hold — used by range allocation to size blocks safely. */
function maxSerialForConstruct(construct) {
  const spec = CONSTRUCTS[construct];
  if (!spec) throw new ImpbError(`unknown construct ${construct}`);
  return Number('9'.repeat(spec.serialLength));
}

module.exports = {
  CONSTRUCTS,
  ROUTING_AI,
  STC_LENGTH,
  ImpbError,
  buildBarcode,
  parseBarcode,
  mod10CheckDigit,
  groupDigits,
  maxSerialForConstruct,
};
